import type { FC } from 'react';
import type { StaticMessageId } from '@gonfalon/messages/ids';
import { Message } from '@gonfalon/messages/Message';
import { Alert, Heading, Text } from '@launchpad-ui/components';

import * as styles from './InferenceErrorMessage.css';

const ERROR_COPY: Record<string, { title: StaticMessageId; body?: StaticMessageId; fallbackBody?: StaticMessageId }> = {
  INFERENCE_UNAVAILABLE: {
    title: 'vega.conversation.errorUnavailableTitle',
    body: 'vega.conversation.errorUnavailableBody',
  },
  budget_exceeded: {
    title: 'vega.conversation.errorBudgetExceededTitle',
    body: 'vega.conversation.errorBudgetExceededBody',
  },
  runtime_error: {
    title: 'vega.conversation.errorRuntimeTitle',
    body: 'vega.conversation.errorRuntimeBody',
  },
  no_inference_key: {
    title: 'vega.conversation.errorNoInferenceKeyTitle',
  },
  bedrock_configuration_error: {
    title: 'vega.conversation.errorBedrockConfigurationTitle',
    fallbackBody: 'vega.conversation.errorBedrockConfigurationBody',
  },
  forbidden: {
    title: 'vega.conversation.errorForbiddenTitle',
    body: 'vega.conversation.errorForbiddenBody',
  },
};

const DEFAULT_COPY = {
  title: 'vega.conversation.errorDefaultTitle',
  body: 'vega.conversation.errorDefaultBody',
} as const;

/**
 * Inline banner rendered when a Vega run ends in a terminal error: either a
 * typed `vega_error` envelope in the message stream (e.g.
 * INFERENCE_UNAVAILABLE when the Bedrock proxy circuit breaker trips) or a
 * conversation reaching status "failed" with the error class the runtime
 * reported (budget_exceeded, runtime_error, ...). Replaces the indefinite
 * "thinking..." spinner with an actionable, human-readable explanation so
 * the user knows to retry instead of waiting forever.
 *
 * Codes with a mapped body show that copy as the body and demote the raw
 * backend message to secondary detail; other codes show the raw message
 * (when present) under their mapped title or the default one.
 */
export const InferenceErrorMessage: FC<{
  errorCode?: string;
  errorMessage?: string;
}> = ({ errorCode, errorMessage }) => {
  const copy = errorCode ? ERROR_COPY[errorCode] : undefined;
  const title = copy?.title ?? DEFAULT_COPY.title;
  const body = copy?.body;
  const fallbackBody = copy?.fallbackBody ?? DEFAULT_COPY.body;
  const detail = body && errorMessage ? errorMessage : undefined;

  return (
    <div className={styles.container} role="status" aria-live="polite">
      <Alert status="error">
        <Heading slot="title">
          <Message id={title} />
        </Heading>
        <Text>{body ? <Message id={body} /> : (errorMessage ?? <Message id={fallbackBody} />)}</Text>
        {detail && <Text className={styles.detail}>{detail}</Text>}
      </Alert>
    </div>
  );
};
